import type { ReactNode } from 'react';

/**
 * 依存を足さない小さな Markdown の描画（U3）。innerHTML を使わず React 要素を組み立てるので、
 * 元の文字列に何が入っていても文字として出る（`<script>`・`onerror=`・`javascript:` は素通りしない）。
 *
 * 対応する記法は AI が書くものと利用者が書くものに絞る。見出し（#〜####）・段落・箇条書き（- / *、
 * 入れ子は 1 段）・番号付き（1.）・表（| で囲むヘッダ行と区切り行）・太字（**）・斜体（*）・
 * インラインコード・コードブロック（言語は無視）・リンク（[text](http(s)://…) だけ）・
 * 水平線（---）・改行。これ以外（引用・画像・脚注など）は文字列のまま出す。
 * 画像は「! ＋ リンク」に崩れないよう、リンクより先に見て生の文字列のまま返す。
 */

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'bullets'; items: BulletItem[] }
  | { kind: 'ordered'; items: string[] }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'code'; code: string }
  | { kind: 'rule' };

interface BulletItem {
  text: string;
  /** 入れ子は 1 段だけ。 */
  children: string[];
}

const HEADING = /^(#{1,4})\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;
/** 入れ子の箇条書き（先頭に空白 1〜4 個）。 */
const NESTED = /^\s{1,4}[-*]\s+(.*)$/;
const ORDERED = /^\d+\.\s+(.*)$/;
const RULE = /^-{3,}$/;
const FENCE = /^\s*```/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const DIVIDER_CELL = /^:?-+:?$/;
/**
 * インライン記法。前から順に見る。
 * 1. インラインコード 2. 画像（対応しないので素通り）3. 太字 4. 斜体 5. リンク（http(s) だけ）。
 * 画像を太字・リンクより先に置くのは、`![図](https://…)` が「! ＋ リンク」に崩れるのを防ぐため。
 */
const INLINE =
  /(`[^`\n]+`)|(!\[[^\]\n]*\]\([^\s)]*\))|(\*\*[^\n]+?\*\*)|(\*[^*\n]+?\*)|(\[[^\]\n]*\]\(https?:\/\/[^\s)]+\))/;

export interface MarkdownProps {
  text: string;
  /** 外側の div に足す class。 */
  className?: string;
}

/** 段落・見出し・箇条書きなどを含む本文を描く。 */
export function Markdown({ text, className }: MarkdownProps) {
  const blocks = parseBlocks(text);
  return (
    <div className={className === undefined ? 'md' : `md ${className}`}>
      {blocks.map((block, index) => renderBlock(block, `b${index}`))}
    </div>
  );
}

/** 1 行だけの文字列（記憶の text など）にインライン記法だけを当てる。 */
export function InlineMarkdown({ text }: { text: string }) {
  return <>{inlineNodes(text, 'i')}</>;
}

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length > 0) blocks.push({ kind: 'paragraph', text: paragraph.join('\n') });
    paragraph = [];
  };
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (line.trim() === '') {
      flush();
      index += 1;
      continue;
    }
    if (FENCE.test(line)) {
      flush();
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test(lines[index] ?? '')) {
        code.push(lines[index] ?? '');
        index += 1;
      }
      // 閉じの印を読み飛ばす（無いまま終わってもここで止まる）。
      index += 1;
      blocks.push({ kind: 'code', code: code.join('\n') });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length, text: heading[2] ?? '' });
      index += 1;
      continue;
    }
    if (RULE.test(line.trim())) {
      flush();
      blocks.push({ kind: 'rule' });
      index += 1;
      continue;
    }
    if (TABLE_ROW.test(line) && isDivider(lines[index + 1])) {
      flush();
      const header = cellsOf(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && TABLE_ROW.test(lines[index] ?? '')) {
        rows.push(cellsOf(lines[index] ?? ''));
        index += 1;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }
    if (BULLET.test(line)) {
      flush();
      const items: BulletItem[] = [];
      while (index < lines.length) {
        const current = lines[index] ?? '';
        const top = BULLET.exec(current);
        if (top) {
          items.push({ text: top[1] ?? '', children: [] });
          index += 1;
          continue;
        }
        const nested = NESTED.exec(current);
        const last = items.at(-1);
        if (nested && last !== undefined) {
          last.children.push(nested[1] ?? '');
          index += 1;
          continue;
        }
        break;
      }
      blocks.push({ kind: 'bullets', items });
      continue;
    }
    if (ORDERED.test(line)) {
      flush();
      const items: string[] = [];
      while (index < lines.length) {
        const found = ORDERED.exec(lines[index] ?? '');
        if (found === null) break;
        items.push(found[1] ?? '');
        index += 1;
      }
      blocks.push({ kind: 'ordered', items });
      continue;
    }
    paragraph.push(line);
    index += 1;
  }
  flush();
  return blocks;
}

function cellsOf(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

/** 表のヘッダ行の次に来る区切り行（| --- | --- |）か。 */
function isDivider(line: string | undefined): boolean {
  if (line === undefined || !TABLE_ROW.test(line)) return false;
  const cells = cellsOf(line);
  return cells.length > 0 && cells.every((cell) => DIVIDER_CELL.test(cell));
}

function renderBlock(block: Block, key: string): ReactNode {
  switch (block.kind) {
    case 'heading': {
      const Tag = (['h1', 'h2', 'h3', 'h4'] as const)[block.level - 1] ?? 'h4';
      return <Tag key={key}>{inlineNodes(block.text, key)}</Tag>;
    }
    case 'paragraph':
      return <p key={key}>{lineNodes(block.text, key)}</p>;
    case 'bullets':
      return (
        <ul key={key}>
          {block.items.map((item, n) => (
            <li key={`${key}-${n}`}>
              {inlineNodes(item.text, `${key}-${n}`)}
              {item.children.length > 0 && (
                <ul>
                  {item.children.map((child, c) => (
                    <li key={`${key}-${n}-${c}`}>{inlineNodes(child, `${key}-${n}-${c}`)}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      );
    case 'ordered':
      return (
        <ol key={key}>
          {block.items.map((item, n) => (
            <li key={`${key}-${n}`}>{inlineNodes(item, `${key}-${n}`)}</li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <table key={key}>
          <thead>
            <tr>
              {block.header.map((cell, n) => (
                <th key={`${key}-h${n}`} scope="col">
                  {inlineNodes(cell, `${key}-h${n}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={`${key}-r${r}`}>
                {row.map((cell, c) => (
                  <td key={`${key}-r${r}-c${c}`}>{inlineNodes(cell, `${key}-r${r}-c${c}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case 'code':
      return (
        <pre key={key}>
          <code>{block.code}</code>
        </pre>
      );
    case 'rule':
      return <hr key={key} />;
  }
}

/** 段落の中の改行を br にする。 */
function lineNodes(text: string, key: string): ReactNode[] {
  return text
    .split('\n')
    .flatMap((line, n) =>
      n === 0 ? inlineNodes(line, `${key}-${n}`) : [<br key={`${key}-br${n}`} />, ...inlineNodes(line, `${key}-${n}`)],
    );
}

function inlineNodes(text: string, key: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // 入れ子（太字の中の斜体など）で lastIndex を共有しないよう、走査のたびに作り直す。
  const pattern = new RegExp(INLINE, 'g');
  let last = 0;
  let count = 0;
  for (let found = pattern.exec(text); found !== null; found = pattern.exec(text)) {
    const token = found[0];
    if (found.index > last) nodes.push(text.slice(last, found.index));
    last = found.index + token.length;
    count += 1;
    const id = `${key}-${count}`;
    if (token.startsWith('`')) nodes.push(<code key={id}>{token.slice(1, -1)}</code>);
    // 画像は対応しない。ここで拾っておかないとリンクの枝が中身に当たってしまう。
    else if (token.startsWith('!')) nodes.push(token);
    else if (token.startsWith('**')) nodes.push(<strong key={id}>{inlineNodes(token.slice(2, -2), id)}</strong>);
    else if (token.startsWith('*')) nodes.push(<em key={id}>{inlineNodes(token.slice(1, -1), id)}</em>);
    else {
      const split = token.indexOf('](');
      const label = token.slice(1, split);
      const href = token.slice(split + 2, -1);
      nodes.push(
        <a key={id} href={href} target="_blank" rel="noreferrer">
          {label === '' ? href : label}
        </a>,
      );
    }
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
